// CUNY Hunter College: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Hunter publishes no Common Data Set: its Office of Institutional
//    Research posts factbooks and points to College Navigator. The
//    first-year counts come from the U.S. Department of Education's IPEDS
//    Admissions survey for Fall 2024, first-time degree-seeking students
//    (College Navigator, unit 190594, which shows the 32,271 applicants and
//    54% admitted; the counts are from the ADM2024 complete data file:
//    APPLCN 32,271, ADMSSN 17,362, ENRLT 2,897).
//    Quirk: College Navigator's admissions note still says CUNY is test
//    optional "through the spring 2027 semester"; CUNY's own Standardized
//    Testing Policy page now states the policy with no end date, and Hunter's
//    freshman page says it is "currently test optional". The page follows
//    Hunter.
//  - Hunter Admissions, Freshman Admission (fall deadlines and notification
//    table, admission criteria, test policy, selective programs). The table
//    carries no year; it is the page as posted for the application open in
//    fall 2026 (the Macaulay dates it lists match Macaulay's 2026 dates).
//  - Macaulay Honors College, Early Decision Program (binding terms; Hunter
//    is one of the eight CUNY colleges it covers).
//  - Hunter Tuition and Payment, Additional Costs (2026-27 indirect costs,
//    living away from home). Tuition and fees are from Hunter's Tuition and
//    Fees page (https://www.hunter.cuny.edu/students/tuition-and-payment/
//    tuition-and-fees/), which gives current rates without naming a year:
//    $6,930 a year in-state, $18,600 out-of-state (at 15 credits a semester),
//    and $225.10 a semester in mandatory fees for full-time undergraduates.
//    The page's cost block links one source, so that page is named in the
//    cost note rather than linked; flagged to the coordinator.
//  - College Scorecard, school id 190594 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=190594#admsns',
        read: '2026-10-01',
    },
    freshman: {
        label: 'Hunter College Admissions, Freshman Admission',
        url: 'https://www.hunter.cuny.edu/students/admissions/undergraduate/apply/freshman/',
        read: '2026-10-01',
    },
    macaulay: {
        label: 'Macaulay Honors College, Early Decision Program',
        url: 'https://macaulay.cuny.edu/admissions/applying-to-macaulay/early-decision-program/',
        read: '2026-10-01',
    },
    costs: {
        label: 'Hunter College, Additional Costs',
        url: 'https://www.hunter.cuny.edu/students/tuition-and-payment/tuition-and-fees/additional-costs/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?190594-CUNY-Hunter-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'cuny-hunter-college',
    kbId: 'cuny_hunter_college',
    name: 'CUNY Hunter College',
    shortName: 'Hunter',
    location: 'New York, NY',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Hunter visual identity page); decoration only.
    branding: {
        primary: '#5F259F',
        secondary: '#FFC72A',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/cuny_hunter_college_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 32271,
        admitted: 17362,
        enrolled: 2897,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Freshmen are admitted to Hunter College, not to a major or program, and Hunter publishes no acceptance rate by major. It posts no Common Data Set, so these counts come from the federal IPEDS survey.',
        source: 'freshman',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply through the CUNY application and may name an intended major, but freshmen are admitted to the college, not to a major. Hunter lists three selective programs:',
        rows: [
            { school: 'Music major', requirement: 'Listed by Hunter as a selective program for freshman applicants.' },
            { school: 'Macaulay Honors College at Hunter', requirement: 'Its own deadlines: Early Decision by October 26 (binding) or regular admission by November 16, both at 6 pm and both hard deadlines.' },
            { school: 'Freshman Honors Scholar Programs', requirement: 'Application and supporting documents by November 16 at 6 pm.' },
        ],
        source: 'freshman',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Priority deadline', value: 'February 1' },
        rows: [
            { plan: 'Freshman, U.S. educated (priority)', deadline: 'February 1', decision: 'Rolling, after December 18', binding: false },
            { plan: 'Freshman, international', deadline: 'December 1', decision: 'Rolling, January to July', binding: false },
            { plan: 'Honors Scholar Program', deadline: 'November 16', decision: 'Rolling, after December 18', binding: false },
            { plan: 'Macaulay Honors College, Early Decision', deadline: 'October 26', decision: 'December 18', binding: true },
            { plan: 'Macaulay Honors College, regular admission', deadline: 'November 16', decision: 'March 11', binding: false },
        ],
        note: 'Hunter keeps reviewing complete freshman applications after February 1 as space allows. A complete application is the CUNY application, all supporting documents and the fee or a waiver. International applicants send supporting documents by December 15; Macaulay regular applicants send them by November 30. CUNY lets New York City public school students apply free from September 15 to November 16, and other New York students from October 15 to November 16.',
        source: 'freshman',
        binding: 'Macaulay Early Decision is binding: if admitted, you commit to attending Macaulay and withdraw all other college applications, and you may not defer. The general Hunter application is not binding.',
        bindingSource: 'macaulay',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Hunter is currently test-optional. It strongly encourages SAT or ACT scores, especially from students applying to merit scholarship programs such as Freshman Honors and Macaulay, but students who do not send scores are not disadvantaged. Admission rests on the whole academic record: GPA, the strength of your courses, and activities and leadership.',
        source: 'freshman',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'freshman',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [6930, 18600] },
            { item: 'Required fees (two semesters)', amounts: [450.2, 450.2] },
            { item: 'Housing (living away from home)', amounts: [20124, 20124] },
            { item: 'Food (lunch)', amounts: [4550, 4550] },
            { item: 'Books and supplies', amounts: [1500, 1500] },
            { item: 'Transportation (MetroCard)', amounts: [1190, 1190] },
            { item: 'Medical', amounts: [2335, 2335] },
            { item: 'Personal expenses', amounts: [2885, 2885] },
        ],
        totals: [39964.2, 51634.2],
        note: 'Hunter\'s budget for a full-time undergraduate living away from home; its living-at-home budget is $16,318 before tuition and fees. Tuition and fees are the current rates on Hunter\'s Tuition and Fees page, which names no year; out-of-state tuition assumes 15 units a semester.',
        source: 'costs',
        where: 'indirect costs, 2026-27',
        netPrice: {
            average: 1051,
            byIncome: [
                { income: '$0 to $30,000', amount: null },
                { income: '$30,001 to $48,000', amount: 158 },
                { income: '$48,001 to $75,000', amount: 3335 },
                { income: '$75,001 to $110,000', amount: 6567 },
                { income: 'Over $110,000', amount: 10600 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. The Department publishes no figure for the $0 to $30,000 band. Your own price comes from Hunter\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
